import {
  isClientGroup,
  isGoal,
  isSubAccount,
  type PortfolioReport,
} from '@interfaces/portfolioReport';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import { isInactiveZeroAccountDecision } from '../audit/decisionPredicates';

const PENDING_WITHDRAW_PATTERN = /pending withdraw-all transfer/i;

const displayName = (
  firstName?: string | null,
  lastName?: string | null,
  entityName?: string | null,
): string => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return entityName || '';
};

export const getReportClientLabel = (report: PortfolioReport): string => {
  const { object } = report;

  if (isSubAccount(object)) {
    return displayName(
      object.account?.user?.firstName,
      object.account?.user?.lastName,
      object.account?.user?.entityName,
    );
  }

  if (isGoal(object)) {
    if (object.householdClientGroup?.name) {
      return object.householdClientGroup.name;
    }

    return displayName(
      object.user?.firstName,
      object.user?.lastName,
      object.user?.entityName,
    );
  }

  if (isClientGroup(object)) {
    return object.clientGroupName || '';
  }

  return '';
};

export const getReportGoalLabel = (report: PortfolioReport): string => {
  const { object } = report;

  if (isGoal(object)) {
    return object.name || '';
  }

  if (isSubAccount(object)) {
    return object.goal?.name || '';
  }

  return '';
};

export const getReportMarketValueCents = (report: PortfolioReport): number => {
  const { object } = report;

  if (isGoal(object) || isSubAccount(object)) {
    return object.statistics?.marketValueCents ?? 0;
  }

  if (isClientGroup(object)) {
    return (
      report.subAccounts?.reduce(
        (sum, subAccountReport) =>
          sum + (subAccountReport.marketValueCents ?? 0),
        0,
      ) ?? 0
    );
  }

  return 0;
};

export const hasCashTrigger = (report: PortfolioReport): boolean =>
  report.transferRebalanceRequired || report.liquidateRebalanceRequired;

const hasAuditLiquidationDecision = (
  audit: OptimizerAuditV2Record | undefined,
  matchesAction: (action: string) => boolean,
): boolean =>
  Boolean(
    audit?.decisions?.some(
      (decision) =>
        decision.stage === 'LIQUIDATION' &&
        !isInactiveZeroAccountDecision(decision) &&
        matchesAction(decision.action),
    ),
  );

export const hasAuditLiquidationTrigger = (
  audit: OptimizerAuditV2Record | undefined,
): boolean =>
  hasAuditLiquidationDecision(
    audit,
    (action) => !PENDING_WITHDRAW_PATTERN.test(action),
  );

export const hasTransferTrigger = (
  report: PortfolioReport | undefined,
  audit?: OptimizerAuditV2Record,
): boolean =>
  Boolean(report?.transferRebalanceRequired) ||
  hasAuditLiquidationDecision(audit, (action) =>
    PENDING_WITHDRAW_PATTERN.test(action),
  );

export const hasLiquidateTrigger = (
  report: PortfolioReport | undefined,
  audit?: OptimizerAuditV2Record,
): boolean =>
  Boolean(report?.liquidateRebalanceRequired) ||
  hasAuditLiquidationDecision(
    audit,
    (action) => !PENDING_WITHDRAW_PATTERN.test(action),
  );

export const isCashEventRebalance = (
  report: PortfolioReport | undefined,
  audit?: OptimizerAuditV2Record,
): boolean =>
  hasTransferTrigger(report, audit) || hasLiquidateTrigger(report, audit);
